import { motion } from "framer-motion";
import { Globe, Award, Users, Shield, Target, Zap, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Reliability",
      description: "99.9% uptime guarantee with redundant infrastructure across multiple data centers."
    },
    {
      icon: Zap,
      title: "Quality",
      description: "Tier 1 carrier partnerships ensuring crystal-clear call quality worldwide."
    },
    {
      icon: Target,
      title: "Transparency",
      description: "No hidden fees, clear pricing, and real-time rate updates."
    },
    {
      icon: Users,
      title: "Support",
      description: "24/7 technical support via Telegram with quick response times."
    }
  ];

  const milestones = [
    { label: "Countries Covered", value: "235+" },
    { label: "Active Routes", value: "53K+" },
    { label: "Happy Customers", value: "500+" },
    { label: "Uptime", value: "99.9%" }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 noise-bg" data-testid="about-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">About CyberX Calls</h1>
            <p className="text-lg text-slate-300">
              Connecting businesses worldwide with premium voice termination services since day one. 
              We're committed to delivering quality, reliability, and competitive rates.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-red-600 py-12">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {milestones.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center text-white"
              >
                <p className="text-4xl md:text-5xl font-bold">{stat.value}</p>
                <p className="text-red-100 mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-light" data-testid="our-story">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-slate-600 text-lg">
                <p>
                  CyberX Calls was founded with a simple mission: to make international voice termination 
                  accessible, affordable, and reliable for businesses of all sizes.
                </p>
                <p>
                  We've built partnerships with Tier 1 carriers and developed direct connections worldwide 
                  to ensure the highest call quality for our customers.
                </p>
                <p>
                  Today, we serve hundreds of customers globally, from small resellers to large call centers, 
                  all benefiting from our competitive rates and reliable service.
                </p>
              </div>
              
              <div className="flex gap-4 mt-8">
                <a href="/portal" rel="noopener noreferrer">
                  <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6">
                    Get Started <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full px-6 border-slate-300">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1770520216107-87b301250f52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHJlZCUyMGFuZCUyMHdoaXRlJTIwZGF0YSUyMG5ldHdvcmslMjBjb25uZWN0aW9ufGVufDB8fHx8MTc3MTE3MjQ5Mnww&ixlib=rb-4.1.0&q=85"
                alt="Global Network"
                className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-alt" data-testid="our-values">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Values</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do at CyberX Calls
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-light" data-testid="why-us">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="https://images.unsplash.com/photo-1758876019427-e2e5bf9a92c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHx3b21hbiUyMHdpdGglMjBoZWFkc2V0JTIwc21pbGluZyUyMGluJTIwb2ZmaWNlfGVufDB8fHx8MTc3MTE3MjUwM3ww&ixlib=rb-4.1.0&q=85"
                alt="Support Team"
                className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why Businesses Choose Us
              </h2>
              
              <ul className="space-y-4 mb-8">
                {[
                  "Competitive rates for 235+ countries",
                  "Multiple quality tiers (Silver, Gold, Platinum)",
                  "Specialized call center routes",
                  "No minimum commitment",
                  "Real-time rate updates",
                  "24/7 Telegram support",
                  "Multiple payment options (USD/EUR)",
                  "Minimum recharge: 100 USD/EUR"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Award className="w-4 h-4 text-red-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              <a href="https://t.me/cyberx007" target="_blank" rel="noopener noreferrer">
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6">
                  <MessageCircle className="mr-2 w-4 h-4" /> Chat With Us
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark" data-testid="about-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Join hundreds of satisfied customers and start terminating calls with competitive rates today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/portal" rel="noopener noreferrer">
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-6 text-lg">
                  Create Account <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/rates">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-slate-600 text-white hover:bg-white/10">
                  View Rates
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
